import { useState, useEffect, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { 
  Play, Pause, Volume2, VolumeX, Radio, Music, 
  Mic2, Calendar, Heart, Send, Menu, X, User, LogOut,
  ChevronRight, ChevronDown, Headphones, Disc, Waves, Upload, Video, Music2,
  Globe, Phone, Mail, FileText, Check, ExternalLink, Eye, EyeOff, Lock, Tv, Settings
} from "lucide-react";
import { Toaster, toast } from "sonner";
import AdminPage from "./AdminPage";
import WeatherNewsPage from "./pages/WeatherNewsPage";
import DJsPage, { DJDetailPage } from "./pages/DJsPage";
import ListenerMapPage from "./pages/ListenerMapPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("hotlive95_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (e) {
      localStorage.removeItem("hotlive95_token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("hotlive95_token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("hotlive95_token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("hotlive95_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Noise Overlay Component
const NoiseOverlay = () => <div className="noise-overlay" />;

// Logo Background Component
const LogoBackground = () => (
  <div 
    className="logo-background"
    aria-hidden="true"
  />
);

// Audio Visualizer Component
const AudioVisualizer = ({ isPlaying }) => {
  const bars = Array.from({ length: 12 }, (_, i) => i);
  
  return (
    <div className="flex items-end gap-1 h-10">
      {bars.map((bar) => (
        <motion.div
          key={bar}
          className="w-1 bg-neon-red rounded-full"
          animate={{
            height: isPlaying ? [10, 40, 20, 35, 15, 30, 25][bar % 7] : 4,
          }}
          transition={{
            duration: 0.4,
            repeat: isPlaying ? Infinity : 0,
            repeatType: "reverse",
            delay: bar * 0.05,
          }}
        />
      ))}
    </div>
  );
};

// Password Input Component with Toggle Visibility
const PasswordInput = ({ value, onChange, placeholder, testId, required = true }) => {
  const [showPassword, setShowPassword] = useState(false);
  
  return (
    <div className="relative">
      <input
        type={showPassword ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="radio-input pr-12"
        required={required}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-chrome-mid hover:text-white transition-colors p-1"
        data-testid={`${testId}-toggle`}
      >
        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
};

// Admin Login Modal Component
const AdminLoginModal = ({ isOpen, onClose, onSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/admin-login`, { username, password });
      localStorage.setItem("hotlive95_admin_token", response.data.access_token);
      toast.success("Welcome, Admin!");
      onSuccess();
    } catch (err) {
      toast.error("Invalid admin credentials");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-surface border border-white/10 p-8 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-login-modal"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-neon-red flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-display text-2xl chrome-text">ADMIN LOGIN</h2>
            <p className="font-mono text-xs text-neon-red">ADMINISTRATIVE USE ONLY</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">USERNAME</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="radio-input"
              required
              placeholder="Enter admin username"
              data-testid="admin-modal-username"
            />
          </div>
          <div>
            <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">PASSWORD</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              testId="admin-modal-password"
            />
          </div>
          <button 
            type="submit" 
            className="chrome-btn w-full"
            disabled={loading}
            data-testid="admin-modal-login-btn"
          >
            {loading ? "Authenticating..." : "Login to Admin"}
          </button>
        </form>

        <button
          onClick={onClose}
          className="mt-4 w-full text-center text-sm text-chrome-mid hover:text-white transition-colors"
        >
          Cancel
        </button>
      </motion.div>
    </motion.div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleAdminSuccess = () => {
    setShowAdminLogin(false);
    navigate('/admin');
  };

  return (
    <>
      <nav className="glass-nav fixed top-0 left-0 right-0 z-50" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4 group" data-testid="nav-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_soul-station/artifacts/r4wkew7h_Untitled%20design.png" 
                alt="Hot Live 95 Detroit"
                className="h-14 md:h-16 w-auto"
              />
              <div className="hidden sm:block">
                <p className="font-display text-lg md:text-xl tracking-wide text-white">Hot Live 95</p>
                <p className="font-mono text-[10px] md:text-xs tracking-widest text-neon-red">DETROIT'S FINEST A.I. RADIO</p>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors" data-testid="nav-home">Home</Link>
              <Link to="/stream" className="nav-link font-body text-sm uppercase tracking-widest text-neon-red hover:text-white transition-colors" data-testid="nav-stream">Stream</Link>
              <Link to="/segments" className="nav-link font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors" data-testid="nav-segments">Segments</Link>
              <Link to="/schedule" className="nav-link font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors" data-testid="nav-schedule">Schedule</Link>
              <Link to="/request" className="nav-link font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors" data-testid="nav-request">Request</Link>
              <Link to="/submit" className="nav-link font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors" data-testid="nav-submit">Submit</Link>
              <Link to="/news" className="nav-link font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors" data-testid="nav-news">Weather</Link>
              <Link to="/djs" className="nav-link font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors" data-testid="nav-djs">DJs</Link>
              <Link to="/listeners" className="nav-link font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors" data-testid="nav-listeners">Listeners</Link>
              <button 
                onClick={() => setShowAdminLogin(true)} 
                className="nav-link font-body text-sm uppercase tracking-widest text-chrome-mid hover:text-white transition-colors"
                data-testid="nav-admin"
              >
                Admin
              </button>
              
              {user ? (
                <div className="flex items-center gap-4">
                  {user.is_subscriber ? (
                    <Link 
                      to="/podcast" 
                      className="primary-btn text-xs text-center leading-tight"
                      data-testid="nav-watch-podcast-btn"
                    >
                      <span className="block">Watch Live</span>
                      <span className="block">Podcast</span>
                    </Link>
                  ) : (
                    <button 
                      onClick={() => setShowAuth(true)} 
                      className="primary-btn text-xs text-center leading-tight"
                      data-testid="nav-upgrade-btn"
                    >
                      <span className="block">Upgrade to</span>
                      <span className="block">subscribe $5/MO</span>
                    </button>
                  )}
                  <span className="text-sm text-chrome-mid font-mono">{user.name}</span>
                  <button onClick={logout} className="outline-btn text-xs" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => setShowAuth(true)} 
                  className="primary-btn text-xs text-center leading-tight"
                  data-testid="nav-subscribe-btn"
                >
                  <span className="block">Live Podcast Access</span>
                  <span className="block">subscribe $5/MO</span>
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-surface border-t border-white/10"
            >
              <div className="px-6 py-4 space-y-4">
                <Link to="/" className="block text-sm uppercase tracking-widest" data-testid="mobile-nav-home">Home</Link>
                <Link to="/stream" className="block text-sm uppercase tracking-widest text-neon-red" data-testid="mobile-nav-stream">Stream</Link>
                <Link to="/segments" className="block text-sm uppercase tracking-widest" data-testid="mobile-nav-segments">Segments</Link>
                <Link to="/schedule" className="block text-sm uppercase tracking-widest" data-testid="mobile-nav-schedule">Schedule</Link>
                <Link to="/request" className="block text-sm uppercase tracking-widest" data-testid="mobile-nav-request">Request</Link>
                <Link to="/submit" className="block text-sm uppercase tracking-widest" data-testid="mobile-nav-submit">Submit Your Music</Link>
                <Link to="/news" className="block text-sm uppercase tracking-widest" data-testid="mobile-nav-news">Weather & News</Link>
                <Link to="/djs" className="block text-sm uppercase tracking-widest" data-testid="mobile-nav-djs">Our DJs</Link>
                <Link to="/listeners" className="block text-sm uppercase tracking-widest" data-testid="mobile-nav-listeners">Live Listeners</Link>
                <button 
                  onClick={() => { setShowAdminLogin(true); setIsOpen(false); }} 
                  className="block text-sm uppercase tracking-widest text-left w-full"
                  data-testid="mobile-nav-admin"
                >
                  Admin
                </button>
                {user ? (
                  user.is_subscriber ? (
                    <Link 
                      to="/podcast" 
                      className="primary-btn w-full text-xs text-center leading-tight"
                      data-testid="mobile-watch-podcast-btn"
                      onClick={() => setIsOpen(false)}
                    >
                      <span className="block">Watch Live Podcast</span>
                    </Link>
                  ) : (
                    <button onClick={() => { setShowAuth(true); setIsOpen(false); }} className="primary-btn w-full text-xs text-center leading-tight" data-testid="mobile-upgrade-btn">
                      <span className="block">Upgrade to Subscribe</span>
                      <span className="block">$5/MO</span>
                    </button>
                  )
                ) : (
                  <button onClick={() => { setShowAuth(true); setIsOpen(false); }} className="primary-btn w-full text-xs text-center leading-tight" data-testid="mobile-subscribe-btn">
                    <span className="block">Live Podcast Access</span>
                    <span className="block">subscribe $5/MO</span>
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Auth Modal */}
      <AnimatePresence>
        {showAuth && (
          <AuthModal 
            mode={authMode} 
            setMode={setAuthMode} 
            onClose={() => setShowAuth(false)} 
          />
        )}
      </AnimatePresence>

      {/* Admin Login Modal */}
      <AnimatePresence>
        {showAdminLogin && (
          <AdminLoginModal 
            isOpen={showAdminLogin}
            onClose={() => setShowAdminLogin(false)}
            onSuccess={handleAdminSuccess}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// Auth Modal Component - Subscription Flow
const AuthModal = ({ mode, setMode, onClose }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back, Subscriber!");
        onClose();
      } else {
        // Register user first
        const userData = await register(email, password, name);
        
        // Create payment link and redirect to Square checkout
        toast.info("Redirecting to secure payment...");
        
        const paymentResponse = await axios.post(`${API}/payments/create-payment-link`, {
          user_id: userData.user.id,
          email: email,
          name: name
        });
        
        if (paymentResponse.data.payment_link_url) {
          // Redirect to Square's hosted checkout page
          window.location.href = paymentResponse.data.payment_link_url;
        } else {
          toast.error("Failed to create payment link");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-surface border border-white/10 p-8 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="auth-modal"
      >
        {mode === "register" ? (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-neon-red flex items-center justify-center">
                <Video className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-display text-2xl chrome-text">SUBSCRIBE</h2>
                <p className="font-mono text-xs text-neon-red">LIVE VIDEO PODCASTS ACCESS</p>
              </div>
            </div>

            {/* Subscription Benefits */}
            <div className="bg-black/30 border border-neon-red/20 p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="font-display text-lg">MONTHLY ACCESS</span>
                <span className="font-display text-2xl text-neon-red">$5<span className="text-sm text-chrome-mid">/mo</span></span>
              </div>
              <ul className="text-sm text-chrome-mid space-y-2">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-neon-red" />
                  <span>Unlimited Live Video Podcasts</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-neon-red" />
                  <span>Exclusive Behind-the-Scenes Content</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-neon-red" />
                  <span>Ad-Free Viewing Experience</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-neon-red" />
                  <span>Early Access to New Shows</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-neon-red" />
                  <span>Cancel Anytime</span>
                </li>
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="radio-input"
                required
                data-testid="auth-name-input"
              />
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="radio-input"
                required
                data-testid="auth-email-input"
              />
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create Password"
                testId="auth-password-input"
              />

              <button 
                type="submit" 
                className="primary-btn w-full"
                disabled={loading}
                data-testid="auth-submit-btn"
              >
                {loading ? "Processing..." : "Subscribe Now - $5/month"}
              </button>
            </form>

            <p className="text-xs text-chrome-mid text-center mt-4">
              By subscribing, you agree to our Terms of Service and Privacy Policy.
            </p>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-neon-red flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="font-display text-2xl chrome-text">SUBSCRIBER LOGIN</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="radio-input"
                required
                data-testid="auth-email-input"
              />
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                testId="auth-password-input"
              />

              <button 
                type="submit" 
                className="primary-btn w-full"
                disabled={loading}
                data-testid="auth-submit-btn"
              >
                {loading ? "..." : "Sign In"}
              </button>
            </form>
          </>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-sm text-chrome-mid hover:text-white transition-colors"
            data-testid="auth-switch-mode"
          >
            {mode === "login" ? "New here? Subscribe now" : "Already a subscriber? Sign in"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Live Player Component - Radio.co Integration
const LivePlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [stationStatus, setStationStatus] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    // Fetch initial data
    fetchStreamUrl();
    fetchNowPlaying();
    fetchStationStatus();
    
    // Poll for updates
    const trackInterval = setInterval(fetchNowPlaying, 15000);
    const statusInterval = setInterval(fetchStationStatus, 30000);
    
    return () => {
      clearInterval(trackInterval);
      clearInterval(statusInterval);
    };
  }, []);

  // Handle audio element updates
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);

  const fetchStreamUrl = async () => {
    try {
      const response = await axios.get(`${API}/radio/stream-url`);
      setStreamUrl(response.data.stream_url);
    } catch (e) {
      // Fallback to default
      setStreamUrl("https://streaming.radio.co/se630121f6/listen");
    }
  };

  const fetchNowPlaying = async () => {
    try {
      const response = await axios.get(`${API}/radio/current-track`);
      setNowPlaying(response.data);
    } catch (e) {
      console.error("Failed to fetch now playing");
    }
  };

  const fetchStationStatus = async () => {
    try {
      const response = await axios.get(`${API}/radio/station-status`);
      setStationStatus(response.data);
    } catch (e) {
      console.error("Failed to fetch station status");
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (audio) {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        // Set volume before playing
        audio.volume = isMuted ? 0 : volume / 100;
        
        // Play the stream
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch(e => {
              console.error("Failed to play audio:", e);
            });
        }
      }
    }
  };

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      className="fixed bottom-0 left-0 right-0 player-bar z-40"
      data-testid="live-player"
    >
      {/* Hidden Audio Element for Radio.co Stream */}
      <audio
        ref={audioRef}
        src="https://streaming.radio.co/se630121f6/listen"
        preload="metadata"
      />

      {/* Progress Bar (pulsing animation when live) */}
      <div className="player-progress">
        <motion.div 
          className="player-progress-fill" 
          animate={isPlaying ? { width: ["0%", "100%"] } : {}}
          transition={isPlaying ? { duration: 30, repeat: Infinity, ease: "linear" } : {}}
          style={!isPlaying ? { width: '0%' } : {}}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Now Playing Info */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="relative w-12 h-12 md:w-14 md:h-14 flex-shrink-0">
              <img 
                src={nowPlaying?.artwork_url || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100"} 
                alt="Album Art"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border border-white/20" />
              {isPlaying && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-neon-red rounded-full animate-pulse" />
              )}
            </div>
            <div className="min-w-0">
              <p className="font-display text-sm md:text-base truncate" data-testid="now-playing-title">
                {nowPlaying?.title || "Hot Live 95 Detroit"}
              </p>
              <p className="text-chrome-mid text-xs md:text-sm truncate" data-testid="now-playing-artist">
                {nowPlaying?.artist || "Live Radio Stream"}
              </p>
            </div>
          </div>

          {/* Center Controls */}
          <div className="flex items-center gap-4">
            <AudioVisualizer isPlaying={isPlaying} />
            <button
              onClick={togglePlay}
              className="w-12 h-12 md:w-14 md:h-14 chrome-btn flex items-center justify-center p-0"
              data-testid="play-pause-btn"
            >
              {isPlaying ? (
                <div className="flex items-center justify-center gap-1">
                  <div className="w-1.5 h-6 bg-black rounded-sm"></div>
                  <div className="w-1.5 h-6 bg-black rounded-sm"></div>
                </div>
              ) : (
                <div 
                  className="ml-1"
                  style={{
                    width: 0,
                    height: 0,
                    borderTop: '12px solid transparent',
                    borderBottom: '12px solid transparent',
                    borderLeft: '20px solid black'
                  }}
                />
              )}
            </button>
            <AudioVisualizer isPlaying={isPlaying} />
          </div>

          {/* Volume & Live */}
          <div className="hidden md:flex items-center gap-4 flex-1 justify-end">
            <div className="live-indicator">
              <span className={`live-dot ${stationStatus?.is_online ? 'animate-pulse' : 'opacity-50'}`} />
              <span className="font-mono text-xs text-neon-red tracking-widest">
                {stationStatus?.is_online ? 'LIVE' : 'OFFLINE'}
              </span>
              {stationStatus?.listeners > 0 && (
                <span className="font-mono text-xs text-chrome-mid ml-2">
                  {stationStatus.listeners} listening
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setIsMuted(!isMuted)}
                className="text-chrome-mid hover:text-white transition-colors"
                data-testid="mute-btn"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={isMuted ? 0 : volume}
                onChange={(e) => {
                  setVolume(Number(e.target.value));
                  if (audioRef.current) {
                    audioRef.current.volume = Number(e.target.value) / 100;
                  }
                }}
                className="volume-slider"
                data-testid="volume-slider"
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Dark Background */}
      <div className="absolute inset-0 bg-background" />

      {/* Renaissance Center Image - Far Right Background at 50% opacity */}
      <div className="absolute right-0 top-0 bottom-0 w-1/2 md:w-2/5">
        <img 
          src="https://images.unsplash.com/photo-1717645944777-5c023452282d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxEZXRyb2l0JTIwUmVuYWlzc2FuY2UlMjBDZW50ZXIlMjBidWlsZGluZyUyMG5pZ2h0fGVufDB8fHx8MTc3MDQ5MjI1OHww&ixlib=rb-4.1.0&q=85&w=1200"
          alt="Detroit Renaissance Center"
          className="h-full w-full object-cover opacity-50"
          data-testid="renaissance-center-bg"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
      </div>

      {/* Red Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-neon-red/5 via-transparent to-neon-red/10" />

      {/* Content - Logo Centered */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        {/* Full Sized Logo - Center Screen */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="mb-8"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_soul-station/artifacts/r4wkew7h_Untitled%20design.png"
            alt="Hot Live 95 Detroit"
            className="w-full max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto"
            data-testid="hero-logo"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <p className="font-mono text-xs md:text-sm tracking-[0.4em] text-neon-red mb-8" data-testid="hero-tagline">
            BROADCASTING THE SOUL OF THE MOTOR CITY
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/segments" className="chrome-btn px-8 py-4 text-sm" data-testid="hero-explore-btn">
            Explore Segments
          </Link>
          <Link to="/request" className="outline-btn px-8 py-4 text-sm" data-testid="hero-request-btn">
            Request a Song
          </Link>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border border-chrome-mid rounded-full flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 bg-neon-red rounded-full" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Segment Card Component
const SegmentCard = ({ segment, index, size = "normal" }) => {
  const sizeClasses = {
    large: "col-span-12 md:col-span-8 row-span-2",
    medium: "col-span-12 md:col-span-4 row-span-2",
    normal: "col-span-12 sm:col-span-6 md:col-span-4",
    small: "col-span-6 md:col-span-3"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className={`${sizeClasses[size]} radio-card group relative overflow-hidden`}
      data-testid={`segment-card-${segment.slug}`}
    >
      <Link to={`/segments/${segment.slug}`}>
        <div className="relative aspect-video">
          <img 
            src={segment.image_url} 
            alt={segment.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          
          {/* Genre Tag */}
          <div className="absolute top-4 left-4">
            <span className="font-mono text-[10px] tracking-[0.2em] text-neon-red bg-black/50 px-3 py-1">
              {segment.genre}
            </span>
          </div>

          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
            <h3 className="font-display text-lg md:text-2xl text-white mb-1">{segment.name}</h3>
            <p className="text-chrome-mid text-xs md:text-sm line-clamp-2">{segment.description}</p>
            
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-2 text-xs text-chrome-mid">
                <Mic2 className="w-3 h-3" />
                <span>{segment.host}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-chrome-mid">
                <Calendar className="w-3 h-3" />
                <span>{segment.schedule}</span>
              </div>
            </div>
          </div>

          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-neon-red/0 group-hover:bg-neon-red/10 transition-colors duration-300 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0 }}
              whileHover={{ scale: 1 }}
              className="w-16 h-16 bg-neon-red rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Play className="w-6 h-6 text-white ml-1" />
            </motion.div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// Featured Segments Section
const FeaturedSegments = () => {
  const [segments, setSegments] = useState([]);

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    try {
      const response = await axios.get(`${API}/segments`);
      setSegments(response.data);
    } catch (e) {
      console.error("Failed to fetch segments");
    }
  };

  const featured = segments.filter(s => s.is_featured);

  return (
    <section className="py-20 px-6" data-testid="featured-segments">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-12">
          <div className="red-line h-12" />
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-2">NOW BROADCASTING</p>
            <h2 className="font-display text-3xl md:text-5xl chrome-text">FEATURED SEGMENTS</h2>
          </div>
        </div>

        <div className="bento-grid">
          {featured.map((segment, index) => (
            <SegmentCard 
              key={segment.id} 
              segment={segment} 
              index={index}
              size={index === 0 ? "large" : index === 1 ? "medium" : "normal"}
            />
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link to="/segments" className="outline-btn inline-flex items-center gap-2" data-testid="view-all-segments">
            View All Segments <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// AI DJ Section
const AIDJSection = () => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [contentType, setContentType] = useState("segment_intro");

  const generateContent = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai/generate-content`, {
        prompt_type: contentType,
        segment: "Hastings Street Heritage"
      });
      setContent(response.data.content);
    } catch (e) {
      toast.error("Failed to generate content");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-surface relative overflow-hidden" data-testid="ai-dj-section">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 right-0 h-px chrome-divider" />
      <div className="absolute bottom-0 left-0 right-0 h-px chrome-divider" />
      
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left - Visual */}
          <div className="relative">
            <div className="aspect-square relative">
              <img 
                src="https://images.unsplash.com/photo-1701491086302-a5bffff6693a?w=800"
                alt="AI DJ Studio"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
              
              {/* Visualizer Overlay */}
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black to-transparent flex items-end justify-center pb-4">
                <div className="flex items-end gap-1 h-16">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-2 bg-neon-red"
                      animate={{ height: [8, 40, 20, 50, 15][i % 5] }}
                      transition={{
                        duration: 0.5,
                        repeat: Infinity,
                        repeatType: "reverse",
                        delay: i * 0.05
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* AI Badge */}
              <div className="absolute top-4 right-4 bg-neon-red px-4 py-2">
                <span className="font-mono text-xs tracking-widest">AI POWERED</span>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-4">MEET YOUR HOST</p>
            <h2 className="font-display text-4xl md:text-6xl chrome-text mb-6">AI DJ MARCUS</h2>
            <p className="text-chrome-mid text-base md:text-lg leading-relaxed mb-8">
              Powered by cutting-edge AI technology, DJ Marcus brings you the authentic sound of Detroit 
              with deep knowledge of the city's musical heritage. From Hastings Street blues to Techno 
              Underground, experience radio hosting like never before.
            </p>

            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {["segment_intro", "artist_fact", "news", "weather"].map((type) => (
                  <button
                    key={type}
                    onClick={() => setContentType(type)}
                    className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                      contentType === type 
                        ? "bg-neon-red text-white" 
                        : "bg-surface-highlight text-chrome-mid hover:text-white"
                    }`}
                    data-testid={`ai-content-type-${type}`}
                  >
                    {type.replace("_", " ")}
                  </button>
                ))}
              </div>

              <button
                onClick={generateContent}
                disabled={loading}
                className="chrome-btn w-full md:w-auto"
                data-testid="generate-ai-content-btn"
              >
                {loading ? "Generating..." : "Generate Script"}
              </button>

              {content && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-black/50 border border-white/10 p-6 mt-4"
                  data-testid="ai-generated-content"
                >
                  <p className="text-chrome-mid italic leading-relaxed">"{content}"</p>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Welcome to the Hot Live 95 family!");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 px-6 relative" data-testid="newsletter-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 warning-stripes" />
      
      <div className="relative max-w-3xl mx-auto text-center">
        <Headphones className="w-12 h-12 text-neon-red mx-auto mb-6" />
        <h2 className="font-display text-3xl md:text-5xl chrome-text mb-4">STAY TUNED</h2>
        <p className="text-chrome-mid mb-8">
          Get exclusive updates, show schedules, and Detroit music news delivered to your inbox.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="radio-input flex-1"
            required
            data-testid="newsletter-email-input"
          />
          <button 
            type="submit" 
            className="primary-btn whitespace-nowrap"
            disabled={loading}
            data-testid="newsletter-submit-btn"
          >
            {loading ? "..." : "Subscribe"}
          </button>
        </form>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-surface border-t border-white/10 py-12 px-6 mb-20" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-neon-red flex items-center justify-center">
                <Radio className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-display text-lg chrome-text">HOT LIVE 95</h3>
                <p className="font-mono text-[8px] text-neon-red tracking-[0.3em]">DETROIT</p>
              </div>
            </div>
            <p className="text-chrome-mid text-sm">
              Broadcasting the soul of the Motor City since 2026.
            </p>
          </div>

          <div>
            <h4 className="font-display text-sm mb-4">SEGMENTS</h4>
            <ul className="space-y-2">
              <li><Link to="/segments/hastings-street-heritage" className="text-chrome-mid text-sm hover:text-neon-red transition-colors">Hastings Street Heritage</Link></li>
              <li><Link to="/segments/motor-city-gritty" className="text-chrome-mid text-sm hover:text-neon-red transition-colors">Motor City Gritty</Link></li>
              <li><Link to="/segments/techno-underground" className="text-chrome-mid text-sm hover:text-neon-red transition-colors">Techno Underground</Link></li>
              <li><Link to="/segments/soul-sessions" className="text-chrome-mid text-sm hover:text-neon-red transition-colors">Soul Sessions</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm mb-4">QUICK LINKS</h4>
            <ul className="space-y-2">
              <li><Link to="/schedule" className="text-chrome-mid text-sm hover:text-neon-red transition-colors">Schedule</Link></li>
              <li><Link to="/request" className="text-chrome-mid text-sm hover:text-neon-red transition-colors">Request a Song</Link></li>
              <li><Link to="/about" className="text-chrome-mid text-sm hover:text-neon-red transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-chrome-mid text-sm hover:text-neon-red transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm mb-4">CONNECT</h4>
            <p className="text-chrome-mid text-sm mb-4">
              Detroit, Michigan<br />
              United States
            </p>
            <p className="font-mono text-xs text-neon-red">
              info@hotlive95detroit.com
            </p>
          </div>
        </div>

        <div className="chrome-divider mb-8" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-chrome-mid text-xs">
            © 2026 Hot Live 95 Detroit. All rights reserved.
          </p>
          <p className="font-mono text-xs text-chrome-dark">
            Powered by AI • Built for Detroit
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
// VU Meter Bar Component
const VUMeterBar = ({ index, isPlaying, volume }) => {
  const baseHeight = 4;
  const maxHeight = 24;
  const heights = [0.6, 0.8, 1, 0.9, 0.7, 0.85, 0.95, 0.75];
  const delays = [0, 0.1, 0.05, 0.15, 0.08, 0.12, 0.03, 0.18];
  
  return (
    <motion.div
      className="w-1 rounded-full"
      style={{
        background: index < 5 ? '#22c55e' : index < 7 ? '#eab308' : '#ef4444',
      }}
      animate={{
        height: isPlaying 
          ? [baseHeight, maxHeight * heights[index] * volume, baseHeight + 4, maxHeight * heights[(index + 3) % 8] * volume]
          : baseHeight,
      }}
      transition={{
        duration: 0.4,
        repeat: isPlaying ? Infinity : 0,
        repeatType: "mirror",
        delay: delays[index],
        ease: "easeInOut",
      }}
    />
  );
};

// Theme Audio Player Component for Homepage - Circular with VU Meter
const ThemeAudioPlayer = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isExpanded, setIsExpanded] = useState(false);

  // Cleanup function to stop audio
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = ''; // Clear the source to fully stop
    }
    setIsPlaying(false);
  };

  // Listen for start event
  useEffect(() => {
    const handler = () => {
      const audio = audioRef.current;
      if (audio) {
        audio.volume = 0.7;
        audio.src = "https://customer-assets.emergentagent.com/job_f24a211d-55e5-4e9e-ad68-862a2f185dc9/artifacts/s0gopq5c_%E2%AD%90%EF%B8%8FDetroit%E2%80%99s%20The%20Hardest%F0%9F%94%A5%20%281%29.mp3";
        audio.loop = true;
        audio.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch((err) => {
            console.log("Theme play failed:", err);
          });
      }
    };
    window.addEventListener('startThemeAudio', handler);
    return () => {
      window.removeEventListener('startThemeAudio', handler);
      // Cleanup audio on unmount
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
        audio.load();
      }
    };
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.src = "https://customer-assets.emergentagent.com/job_f24a211d-55e5-4e9e-ad68-862a2f185dc9/artifacts/s0gopq5c_%E2%AD%90%EF%B8%8FDetroit%E2%80%99s%20The%20Hardest%F0%9F%94%A5%20%281%29.mp3";
        audioRef.current.play().catch(console.error);
        setIsPlaying(true);
      }
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  return (
    <div className="fixed bottom-24 right-6 z-50" data-testid="theme-audio-player">
      <audio
        ref={audioRef}
        preload="none"
      />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative"
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* Expanded Panel with VU Meter */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, x: 20, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-black/90 backdrop-blur-md border border-chrome-dark rounded-xl p-4 shadow-2xl"
            >
              {/* VU Meter Display */}
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-end gap-1 h-8 px-2">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <VUMeterBar key={i} index={i} isPlaying={isPlaying} volume={volume} />
                  ))}
                </div>
                
                {/* VU Label */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-chrome-mid">-20</span>
                  <div className="w-16 h-0.5 bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded-full" />
                  <span className="text-[10px] font-mono text-chrome-mid">+3</span>
                </div>
                
                {/* Volume Slider */}
                <div className="flex items-center gap-2 w-full">
                  <Volume2 className="w-3 h-3 text-chrome-mid" />
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="flex-1 h-1 bg-chrome-dark rounded-full appearance-none cursor-pointer accent-neon-red"
                    style={{
                      background: `linear-gradient(to right, #ff1a1a ${volume * 100}%, #333 ${volume * 100}%)`
                    }}
                    data-testid="theme-audio-volume"
                  />
                </div>
                
                <span className="text-[10px] font-mono text-neon-red tracking-widest">
                  STATION THEME
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Circular Button */}
        <motion.button
          onClick={togglePlay}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="relative w-16 h-16 rounded-full flex items-center justify-center shadow-lg shadow-black/50"
          style={{
            background: 'radial-gradient(circle at 30% 30%, #444, #1a1a1a)',
            border: '3px solid #333',
            boxShadow: isPlaying 
              ? '0 0 20px rgba(255, 26, 26, 0.5), inset 0 2px 4px rgba(255,255,255,0.1)' 
              : '0 4px 20px rgba(0,0,0,0.5), inset 0 2px 4px rgba(255,255,255,0.1)',
          }}
          data-testid="theme-audio-play-btn"
        >
          {/* Chrome Ring */}
          <div 
            className="absolute inset-1 rounded-full"
            style={{
              background: 'linear-gradient(145deg, #555 0%, #222 50%, #444 100%)',
              boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2), inset 0 -1px 2px rgba(0,0,0,0.5)',
            }}
          />
          
          {/* Inner Circle with VU indicator */}
          <div 
            className="absolute inset-2 rounded-full flex items-center justify-center overflow-hidden"
            style={{
              background: 'radial-gradient(circle at 50% 30%, #2a2a2a, #111)',
              boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.8)',
            }}
          >
            {/* Mini VU Meter inside button when playing */}
            {isPlaying ? (
              <div className="flex items-end gap-0.5 h-6">
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    className="w-1 rounded-full"
                    style={{
                      background: i < 3 ? '#22c55e' : i < 4 ? '#eab308' : '#ef4444',
                    }}
                    animate={{
                      height: [3, 12 + Math.random() * 8, 5, 10 + Math.random() * 6],
                    }}
                    transition={{
                      duration: 0.3,
                      repeat: Infinity,
                      repeatType: "mirror",
                      delay: i * 0.08,
                    }}
                  />
                ))}
              </div>
            ) : (
              <Play className="w-6 h-6 text-chrome-light ml-1" />
            )}
          </div>
          
          {/* Glowing ring when playing */}
          {isPlaying && (
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-neon-red"
              animate={{
                opacity: [0.5, 1, 0.5],
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          )}
        </motion.button>
        
        {/* Status indicator dot */}
        <div 
          className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-black ${isPlaying ? 'bg-green-500' : 'bg-chrome-mid'}`}
          style={{
            boxShadow: isPlaying ? '0 0 8px #22c55e' : 'none',
          }}
        />
      </motion.div>
    </div>
  );
};

// Splash Overlay - Fullscreen entry point (rendered at top level)

const HomePage = () => {
  const navigate = useNavigate();

  // Auto-play theme and navigate to stream after 10 seconds
  useEffect(() => {
    window.dispatchEvent(new Event('startThemeAudio'));
    const timer = setTimeout(() => {
      navigate('/stream?autoplay=true');
    }, 10000);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <main>
      <HeroSection />
      <FeaturedSegments />
      <AIDJSection />
      <NewsletterSection />
      <Footer />
      <ThemeAudioPlayer />
    </main>
  );
};

// Stream Test Page - Diagnostic Tool for Broadcasting Setup
const StreamTestPage = () => {
  const [status, setStatus] = useState({ checking: true });
  const [streamData, setStreamData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLevels, setAudioLevels] = useState([0, 0, 0, 0, 0]);
  const audioRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);

  const checkStreamStatus = async () => {
    setStatus({ checking: true });
    try {
      // Use backend API to avoid CORS issues
      const response = await axios.get(`${API}/radio/station-status`);
      const data = response.data;
      
      setStreamData(data);
      setStatus({
        checking: false,
        online: data.is_online,
        source: data.source || 'Radio.co',
        listeners: data.listeners || 0,
        track: data.current_track?.title || 'No track info',
        artwork: data.current_track?.artwork_url || null
      });
    } catch (error) {
      setStatus({
        checking: false,
        online: false,
        error: error.message
      });
    }
  };

  useEffect(() => {
    checkStreamStatus();
    const interval = setInterval(checkStreamStatus, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const handlePlay = () => {
    if (audioRef.current) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          toast.success("Stream playing!");
        })
        .catch(err => {
          toast.error("Could not play: " + err.message);
        });
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  return (
    <main className="min-h-screen bg-background pt-24 pb-16 px-6">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="font-display text-3xl chrome-text mb-2">STREAM TEST PAGE</h1>
          <p className="text-chrome-mid">Diagnostic tool to verify your broadcast is working</p>
        </motion.div>

        {/* Connection Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface border border-white/10 rounded-lg p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-mono text-lg text-white">Radio.co Stream Status</h2>
            <button 
              onClick={checkStreamStatus}
              className="text-xs text-neon-red hover:underline"
            >
              Refresh
            </button>
          </div>

          {status.checking ? (
            <div className="flex items-center gap-3 text-chrome-mid">
              <div className="w-4 h-4 border-2 border-chrome-mid border-t-transparent rounded-full animate-spin"></div>
              Checking stream status...
            </div>
          ) : (
            <div className="space-y-4">
              {/* Online Status */}
              <div className="flex items-center gap-4 p-4 bg-black/30 rounded-lg">
                <div className={`w-6 h-6 rounded-full ${status.online ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                <div>
                  <p className={`font-bold text-lg ${status.online ? 'text-green-400' : 'text-red-400'}`}>
                    {status.online ? '✓ STREAM IS ONLINE' : '✗ STREAM IS OFFLINE'}
                  </p>
                  <p className="text-chrome-mid text-sm">
                    {status.online ? 'Your broadcast is being received by Radio.co' : 'No broadcast detected - check BUTT connection'}
                  </p>
                </div>
              </div>

              {/* Stream Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-black/30 rounded">
                  <p className="text-chrome-mid text-xs">SOURCE TYPE</p>
                  <p className="text-white font-mono">{status.source || 'N/A'}</p>
                </div>
                <div className="p-3 bg-black/30 rounded">
                  <p className="text-chrome-mid text-xs">LISTENERS</p>
                  <p className="text-white font-mono">{status.listeners}</p>
                </div>
              </div>

              {/* Current Track */}
              <div className="p-4 bg-black/30 rounded-lg">
                <p className="text-chrome-mid text-xs mb-2">NOW PLAYING</p>
                <div className="flex items-center gap-4">
                  {status.artwork && (
                    <img src={status.artwork} alt="Album art" className="w-16 h-16 rounded" />
                  )}
                  <p className="text-white font-medium">{status.track}</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Audio Test Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-surface border border-white/10 rounded-lg p-6 mb-6"
        >
          <h2 className="font-mono text-lg text-white mb-4">Audio Test</h2>
          
          {/* Use Radio.co iframe player instead of HTML5 audio */}
          <div className="flex flex-col items-center gap-4">
            <div className="w-full max-w-sm bg-black rounded-lg overflow-hidden">
              <iframe 
                src="https://embed.radio.co/player/da5e972.html"
                width="100%"
                height="180"
                frameBorder="0"
                allow="autoplay"
                style={{ border: 'none' }}
                title="Hot Live 95 Detroit - Audio Test"
              ></iframe>
            </div>
            <p className="text-chrome-mid text-sm text-center">
              Click the PLAY button in the player above to test audio
            </p>
            
            {/* Alternative: Direct stream link */}
            <a 
              href="https://streaming.radio.co/se630121f6/listen"
              target="_blank"
              rel="noreferrer"
              className="text-neon-red text-sm hover:underline flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Or open stream in external player (VLC, etc.)
            </a>
          </div>
        </motion.div>

        {/* Setup Checklist */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-surface border border-white/10 rounded-lg p-6 mb-6"
        >
          <h2 className="font-mono text-lg text-white mb-4">Broadcasting Checklist</h2>
          
          <div className="space-y-3">
            <ChecklistItem 
              label="PlayoutOne is playing audio" 
              hint="Music should be playing in PlayoutOne"
            />
            <ChecklistItem 
              label="Virtual Audio Cable installed" 
              hint="VB-Cable or similar routing audio"
            />
            <ChecklistItem 
              label="BUTT is connected to Radio.co" 
              hint="BUTT should show 'Connected' status"
              settings="Host: denim.radio.co | Port: 5188"
            />
            <ChecklistItem 
              label="Radio.co shows LIVE status" 
              hint="Check your Radio.co dashboard"
              status={status.online}
            />
            <ChecklistItem 
              label="Audio plays on this page" 
              hint="Click the play button above to test"
              status={isPlaying}
            />
          </div>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-surface border border-white/10 rounded-lg p-6"
        >
          <h2 className="font-mono text-lg text-white mb-4">Quick Links</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a 
              href="https://denim.radio.co" 
              target="_blank" 
              rel="noreferrer"
              className="flex items-center gap-2 p-3 bg-black/30 rounded hover:bg-black/50 transition-colors"
            >
              <ExternalLink className="w-4 h-4 text-neon-red" />
              <span className="text-white">Radio.co Dashboard</span>
            </a>
            <a 
              href="https://streaming.radio.co/se630121f6/listen" 
              target="_blank" 
              rel="noreferrer"
              className="flex items-center gap-2 p-3 bg-black/30 rounded hover:bg-black/50 transition-colors"
            >
              <Radio className="w-4 h-4 text-neon-red" />
              <span className="text-white">Direct Stream URL</span>
            </a>
            <Link 
              to="/stream"
              className="flex items-center gap-2 p-3 bg-black/30 rounded hover:bg-black/50 transition-colors"
            >
              <Headphones className="w-4 h-4 text-neon-red" />
              <span className="text-white">Main Stream Page</span>
            </Link>
            <a 
              href="https://danielnoethen.de/butt/" 
              target="_blank" 
              rel="noreferrer"
              className="flex items-center gap-2 p-3 bg-black/30 rounded hover:bg-black/50 transition-colors"
            >
              <ExternalLink className="w-4 h-4 text-neon-red" />
              <span className="text-white">Download BUTT</span>
            </a>
          </div>
        </motion.div>

        {/* Your Credentials (Hidden by default) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-6 text-center"
        >
          <details className="text-left bg-surface border border-yellow-500/30 rounded-lg">
            <summary className="p-4 cursor-pointer text-yellow-500 font-mono text-sm">
              Show BUTT Configuration (for admin only)
            </summary>
            <div className="p-4 pt-0 font-mono text-sm space-y-1">
              <p className="text-chrome-mid">Server Type: <span className="text-white">ShoutCast</span></p>
              <p className="text-chrome-mid">Host: <span className="text-white">denim.radio.co</span></p>
              <p className="text-chrome-mid">Port: <span className="text-white">5188</span></p>
              <p className="text-chrome-mid">Password: <span className="text-white">900cd8b23dc7</span></p>
            </div>
          </details>
        </motion.div>
      </div>
    </main>
  );
};

// Checklist Item Component
const ChecklistItem = ({ label, hint, settings, status }) => (
  <div className="flex items-start gap-3 p-3 bg-black/30 rounded">
    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
      status === true ? 'border-green-500 bg-green-500' : 
      status === false ? 'border-red-500' : 'border-chrome-mid'
    }`}>
      {status === true && <Check className="w-3 h-3 text-white" />}
    </div>
    <div className="flex-1">
      <p className="text-white text-sm">{label}</p>
      <p className="text-chrome-dark text-xs">{hint}</p>
      {settings && <p className="text-yellow-500/70 text-xs mt-1 font-mono">{settings}</p>}
    </div>
  </div>
);

// Stream Page - Radio.co Player with Diagnostics
const StreamPage = () => {
  const [stationStatus, setStationStatus] = useState(null);
  const [streamConfig, setStreamConfig] = useState(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [audioError, setAudioError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const audioRef = useRef(null);
  const streamAudioRef = useRef(null);
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const shouldAutoplay = searchParams.get('autoplay') === 'true';

  useEffect(() => {
    fetchStationStatus();
    fetchStreamConfig();
    fetchNowPlaying();
    
    const statusInterval = setInterval(fetchStationStatus, 30000);
    const nowPlayingInterval = setInterval(fetchNowPlaying, 15000);
    
    return () => {
      clearInterval(statusInterval);
      clearInterval(nowPlayingInterval);
    };
  }, []);

  // Auto-play stream if coming from homepage transition
  useEffect(() => {
    if (shouldAutoplay && streamAudioRef.current) {
      const tryPlay = () => {
        streamAudioRef.current.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch((err) => {
            console.log("Stream autoplay attempt:", err);
          });
      };
      // Small delay to ensure audio element is loaded
      const timer = setTimeout(tryPlay, 800);
      return () => clearTimeout(timer);
    }
  }, [shouldAutoplay]);

  const fetchStationStatus = async () => {
    try {
      const response = await axios.get(`${API}/radio/station-status`);
      setStationStatus(response.data);
    } catch (e) {
      console.error("Failed to fetch station status");
    }
  };

  const fetchStreamConfig = async () => {
    try {
      const response = await axios.get(`${API}/podcast/stream-config`);
      setStreamConfig(response.data);
    } catch (e) {
      console.error("Failed to fetch stream config");
    }
  };

  const fetchNowPlaying = async () => {
    try {
      const response = await axios.get(`${API}/now-playing`);
      setNowPlaying(response.data);
    } catch (e) {
      console.error("Failed to fetch now playing");
    }
  };

  const handlePlay = () => {
    if (audioRef.current) {
      setAudioError(null);
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          toast.success("Stream playing!");
        })
        .catch(err => {
          setAudioError(err.message);
          toast.error("Could not play stream: " + err.message);
        });
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioError = (e) => {
    const error = e.target.error;
    let errorMsg = "Unknown error";
    if (error) {
      switch(error.code) {
        case 1: errorMsg = "Playback aborted"; break;
        case 2: errorMsg = "Network error - check your connection"; break;
        case 3: errorMsg = "Decoding error - stream format issue"; break;
        case 4: errorMsg = "Stream not supported by browser"; break;
        default: errorMsg = error.message || "Unknown error";
      }
    }
    setAudioError(errorMsg);
  };

  const isSubscriber = user?.is_subscriber === true;

  return (
    <main className="min-h-screen relative" data-testid="stream-page">

      {/* Upload & Submissions Button - Upper Left Corner */}
      <Link
        to="/submit"
        className="fixed top-28 left-4 z-50 bg-neon-red hover:bg-red-700 text-white px-5 py-3 rounded flex items-center gap-2 shadow-xl shadow-neon-red/40 transition-all hover:scale-105 border border-red-400/30"
        data-testid="upload-submissions-btn"
        style={{ boxShadow: '0 0 15px rgba(255,0,0,0.4)' }}
      >
        <Upload className="w-5 h-5" />
        <span className="font-mono text-sm font-bold tracking-wide">UPLOAD &amp; SUBMISSIONS</span>
      </Link>

      {/* Detroit Skyline Background */}
      <div className="fixed inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1717645944777-5c023452282d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxEZXRyb2l0JTIwUmVuYWlzc2FuY2UlMjBDZW50ZXIlMjBidWlsZGluZyUyMG5pZ2h0fGVufDB8fHx8MTc3MDQ5MjI1OHww&ixlib=rb-4.1.0&q=85&w=1920"
          alt="Detroit Skyline"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-background" />
      </div>

      {/* Content */}
      <div className="relative z-10 pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Station Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_soul-station/artifacts/r4wkew7h_Untitled%20design.png"
              alt="Hot Live 95 Detroit"
              className="w-full max-w-lg mx-auto mb-6"
              data-testid="stream-logo"
            />
            
            {/* Live Status Badge */}
            <div className="inline-flex items-center gap-2 bg-neon-red/20 px-4 py-2 border border-neon-red mb-4">
              <span className={`w-3 h-3 rounded-full ${stationStatus?.is_online ? 'bg-neon-red animate-pulse' : 'bg-chrome-mid'}`}></span>
              <span className="font-mono text-sm text-neon-red tracking-widest">
                {stationStatus?.is_online ? 'ON AIR' : 'READY'}
              </span>
              {stationStatus?.listeners > 0 && (
                <span className="font-mono text-sm text-chrome-mid ml-2">
                  • {stationStatus.listeners} listening
                </span>
              )}
            </div>
          </motion.div>

          {/* Stream Control Panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-surface/80 backdrop-blur-md border border-white/10 p-8 mb-8"
          >
            <div className="flex flex-col items-center gap-6">
              <p className="font-mono text-xs tracking-widest text-chrome-mid">LIVE RADIO STREAM</p>
              
              {/* Now Playing - Real Metadata */}
              {nowPlaying && (nowPlaying.track_title || nowPlaying.artist) && (
                <div className="bg-black/60 border border-neon-red/20 rounded-lg p-4 w-full max-w-md" data-testid="now-playing-panel">
                  <p className="font-mono text-[10px] tracking-widest text-neon-red mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-neon-red rounded-full animate-pulse"></span>
                    NOW PLAYING
                  </p>
                  <div className="flex items-center gap-4">
                    {nowPlaying.artwork_url ? (
                      <img 
                        src={nowPlaying.artwork_url} 
                        alt="Album art" 
                        className="w-16 h-16 rounded object-cover border border-white/10"
                        data-testid="now-playing-artwork"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded bg-neon-red/20 flex items-center justify-center border border-neon-red/30">
                        <Music className="w-8 h-8 text-neon-red" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate" data-testid="now-playing-title">
                        {nowPlaying.track_title}
                      </p>
                      <p className="text-chrome-mid text-sm truncate" data-testid="now-playing-artist">
                        {nowPlaying.artist}
                      </p>
                      {nowPlaying.listeners > 0 && (
                        <p className="text-chrome-dark text-xs mt-1">
                          {nowPlaying.listeners} listening now
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Audio Player */}
              <div className="w-full max-w-md space-y-4">
                {/* HTML5 Audio Player */}
                <div className="bg-black/50 rounded-lg p-4">
                  <p className="text-yellow-400 text-sm font-bold mb-3 text-center">
                    {isPlaying ? '🔊 NOW STREAMING LIVE 🔊' : '👇 CLICK PLAY TO LISTEN 👇'}
                  </p>
                  <audio 
                    ref={streamAudioRef}
                    controls 
                    className="w-full"
                    style={{ height: '50px' }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  >
                    <source src="https://streaming.radio.co/se630121f6/listen" type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>

                {/* External Player Button */}
                <a 
                  href="https://streaming.radio.co/se630121f6/listen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="chrome-btn px-6 py-4 flex items-center justify-center gap-2 text-center hover:scale-105 transition-transform w-full"
                >
                  <Radio className="w-5 h-5" />
                  OPEN IN EXTERNAL PLAYER (VLC)
                </a>

              </div>
              
              <p className="text-chrome-mid text-sm text-center">
                Broadcasting live from Detroit - The Motor City
              </p>
              
              {/* Diagnostic Toggle */}
              <button 
                onClick={() => setShowDiagnostics(!showDiagnostics)}
                className="text-xs text-chrome-dark hover:text-chrome-mid transition-colors"
              >
                {showDiagnostics ? 'Hide' : 'Show'} Stream Diagnostics
              </button>
            </div>
          </motion.div>
          
          {/* Diagnostics Panel */}
          {showDiagnostics && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-surface/80 backdrop-blur-md border border-white/10 p-6 mb-8"
            >
              <h3 className="font-mono text-sm text-neon-red mb-4">STREAM DIAGNOSTICS</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-chrome-mid">Station Status:</span>
                  <span className={stationStatus?.is_online ? 'text-green-400' : 'text-red-400'}>
                    {stationStatus?.is_online ? 'ONLINE' : 'OFFLINE'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-chrome-mid">Listeners:</span>
                  <span className="text-white">{stationStatus?.listeners || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-chrome-mid">Stream URL:</span>
                  <span className="text-white text-xs">stream.zeno.fm/nzkasevvfefuv</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-chrome-mid">Player Status:</span>
                  <span className={isPlaying ? 'text-green-400' : 'text-yellow-400'}>
                    {isPlaying ? 'PLAYING' : 'STOPPED'}
                  </span>
                </div>
                {audioError && (
                  <div className="flex justify-between">
                    <span className="text-chrome-mid">Last Error:</span>
                    <span className="text-red-400">{audioError}</span>
                  </div>
                )}
                
                {/* Alternative Stream Test Links */}
                <div className="pt-4 border-t border-white/10">
                  <p className="text-chrome-mid mb-2">Test Stream:</p>
                  <div className="flex flex-wrap gap-2">
                    <a href="https://stream.zeno.fm/nzkasevvfefuv" target="_blank" rel="noreferrer" className="px-3 py-1 bg-chrome-dark rounded text-xs hover:bg-chrome-mid">Zeno.fm Stream</a>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Broadcast Info Panel */}
          {/* Podcast Section - Always Visible */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="text-center mb-6">
              <h3 className="font-display text-2xl chrome-text flex items-center justify-center gap-3">
                <Tv className="w-6 h-6 text-neon-red" />
                LIVE VIDEO PODCAST
              </h3>
              <p className="text-chrome-mid text-sm mt-2">
                {isSubscriber ? "Watch exclusive live content" : "Subscribe for $5/month to access live video podcasts"}
              </p>
            </div>

            <div className="bg-surface/80 backdrop-blur-md border border-white/10">
              {isSubscriber ? (
                // Subscriber View - Show Video
                <div>
                  {streamConfig?.youtube_url ? (
                    <div className="aspect-video">
                      <iframe
                        src={streamConfig.youtube_url.includes('embed') 
                          ? streamConfig.youtube_url 
                          : streamConfig.youtube_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')
                        }
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Hot Live 95 Detroit - Live Podcast"
                      ></iframe>
                    </div>
                  ) : (
                    <div className="aspect-video flex flex-col items-center justify-center p-8">
                      <Tv className="w-16 h-16 text-chrome-dark mb-4" />
                      <p className="text-chrome-mid text-lg">No live podcast scheduled</p>
                      <p className="text-chrome-dark text-sm mt-2">Check back for upcoming shows!</p>
                      {streamConfig?.next_show_time && (
                        <p className="text-neon-red text-sm mt-4">Next show: {streamConfig.next_show_time}</p>
                      )}
                    </div>
                  )}
                  {streamConfig?.is_live && (
                    <div className="p-4 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-neon-red rounded-full animate-pulse"></span>
                        <span className="font-mono text-sm text-neon-red">LIVE NOW</span>
                        <span className="text-chrome-mid text-sm ml-2">
                          {streamConfig.show_title || "Live Podcast"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Non-Subscriber View - Show Subscribe Prompt with Lock Graphic
                <div className="aspect-video flex flex-col items-center justify-center p-8 text-center">
                  <Lock className="w-20 h-20 text-neon-red mb-6" />
                  <h4 className="font-display text-2xl chrome-text mb-3">SUBSCRIBERS ONLY</h4>
                  <p className="text-chrome-mid mb-6 max-w-md text-base">
                    Get exclusive access to live video podcasts, behind-the-scenes content, and more!
                  </p>
                  <Link 
                    to="/podcast" 
                    className="chrome-btn px-10 py-4 text-lg flex items-center gap-2 hover:scale-105 transition-transform"
                    data-testid="subscribe-podcast-btn"
                  >
                    <Tv className="w-5 h-5" />
                    Subscribe - $5/month
                  </Link>
                </div>
              )}
            </div>
          </motion.div>

        </div>
      </div>
    </main>
  );
};

// Segments Page
const SegmentsPage = () => {
  const [segments, setSegments] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    try {
      const response = await axios.get(`${API}/segments`);
      setSegments(response.data);
    } catch (e) {
      console.error("Failed to fetch segments");
    }
  };

  const genres = ["all", ...new Set(segments.map(s => s.genre))];
  const filtered = filter === "all" ? segments : segments.filter(s => s.genre === filter);

  return (
    <main className="pt-24 pb-32 px-6" data-testid="segments-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-2">TUNE IN</p>
          <h1 className="font-display text-4xl md:text-6xl chrome-text mb-8">ALL SEGMENTS</h1>
        </motion.div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-12">
          {genres.map((genre) => (
            <button
              key={genre}
              onClick={() => setFilter(genre)}
              className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                filter === genre 
                  ? "bg-neon-red text-white" 
                  : "bg-surface-highlight text-chrome-mid hover:text-white border border-white/10"
              }`}
              data-testid={`filter-${genre}`}
            >
              {genre}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((segment, index) => (
            <SegmentCard key={segment.id} segment={segment} index={index} />
          ))}
        </div>
      </div>
    </main>
  );
};

// Segment Detail Page
const SegmentDetailPage = () => {
  const [segment, setSegment] = useState(null);
  const slug = window.location.pathname.split("/").pop();

  useEffect(() => {
    fetchSegment();
  }, [slug]);

  const fetchSegment = async () => {
    try {
      const response = await axios.get(`${API}/segments/${slug}`);
      setSegment(response.data);
    } catch (e) {
      console.error("Failed to fetch segment");
    }
  };

  if (!segment) return <div className="pt-32 text-center">Loading...</div>;

  return (
    <main className="pt-24 pb-32" data-testid="segment-detail-page">
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img 
          src={segment.image_url} 
          alt={segment.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-7xl mx-auto">
            <span className="font-mono text-xs tracking-[0.3em] text-neon-red bg-black/50 px-3 py-1 inline-block mb-4">
              {segment.genre}
            </span>
            <h1 className="font-display text-4xl md:text-6xl text-white mb-4">{segment.name}</h1>
            <div className="flex flex-wrap items-center gap-6 text-chrome-mid">
              <div className="flex items-center gap-2">
                <Mic2 className="w-4 h-4" />
                <span>{segment.host}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{segment.schedule}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <p className="text-lg md:text-xl text-chrome-mid leading-relaxed mb-8">
          {segment.description}
        </p>

        <div className="bg-surface border border-white/10 p-6 md:p-8">
          <h3 className="font-display text-xl mb-4">ABOUT THIS SEGMENT</h3>
          <p className="text-chrome-mid leading-relaxed">
            Join us for {segment.name}, where we celebrate the rich musical heritage of Detroit. 
            Hosted by {segment.host}, this segment airs {segment.schedule}. 
            Experience the best of {segment.genre.toLowerCase()} music, curated for true enthusiasts.
          </p>
        </div>

        <div className="mt-8 flex gap-4">
          <Link to="/request" className="primary-btn" data-testid="request-song-from-segment">
            Request a Song
          </Link>
          <Link to="/segments" className="outline-btn" data-testid="back-to-segments">
            All Segments
          </Link>
        </div>
      </div>
    </main>
  );
};

// Schedule Page
const SchedulePage = () => {
  const [schedule, setSchedule] = useState([]);
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/schedule`);
      setSchedule(response.data);
    } catch (e) {
      console.error("Failed to fetch schedule");
    }
  };

  return (
    <main className="pt-24 pb-32 px-6" data-testid="schedule-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-2">PROGRAMMING</p>
          <h1 className="font-display text-4xl md:text-6xl chrome-text mb-8">WEEKLY SCHEDULE</h1>
        </motion.div>

        <div className="space-y-8">
          {days.map((day) => {
            const dayShows = schedule.filter(s => s.day_of_week === day);
            if (dayShows.length === 0) return null;

            return (
              <div key={day} className="bg-surface border border-white/10 p-6">
                <h3 className="font-display text-xl mb-4 text-neon-red">{day.toUpperCase()}</h3>
                <div className="space-y-4">
                  {dayShows.map((show) => (
                    <div key={show.id} className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-black/30 border-l-2 border-neon-red">
                      <div>
                        <h4 className="font-display text-lg">{show.segment_name}</h4>
                        <p className="text-chrome-mid text-sm">{show.description}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-sm text-chrome-mid">
                          {show.start_time} - {show.end_time}
                        </span>
                        <span className="text-xs text-neon-red bg-neon-red/10 px-2 py-1">
                          {show.host}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
};

// Song Request Page
const RequestPage = () => {
  const [formData, setFormData] = useState({
    song_title: "",
    artist: "",
    genre: "Blues",
    message: "",
    user_name: "",
    user_email: ""
  });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        user_name: user.name,
        user_email: user.email
      }));
    }
  }, [user]);

  const genres = ["Blues", "R&B", "Jazz", "Gospel", "Rock", "Soft Rock", "Neo Soul", "Techno"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/requests`, formData);
      toast.success("Song request submitted! We'll play it soon.");
      setFormData({
        song_title: "",
        artist: "",
        genre: "Blues",
        message: "",
        user_name: user?.name || "",
        user_email: user?.email || ""
      });
    } catch (err) {
      toast.error("Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24 pb-32 px-6" data-testid="request-page">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-2">YOUR VOICE MATTERS</p>
          <h1 className="font-display text-4xl md:text-6xl chrome-text mb-4">REQUEST A SONG</h1>
          <p className="text-chrome-mid mb-8">
            Want to hear your favorite track on Hot Live 95? Submit your request and our AI DJs will queue it up!
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">SONG TITLE *</label>
              <input
                type="text"
                value={formData.song_title}
                onChange={(e) => setFormData({...formData, song_title: e.target.value})}
                className="radio-input"
                required
                data-testid="request-song-title"
              />
            </div>
            <div>
              <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">ARTIST *</label>
              <input
                type="text"
                value={formData.artist}
                onChange={(e) => setFormData({...formData, artist: e.target.value})}
                className="radio-input"
                required
                data-testid="request-artist"
              />
            </div>
          </div>

          <div>
            <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">GENRE</label>
            <div className="flex flex-wrap gap-2">
              {genres.map((genre) => (
                <button
                  key={genre}
                  type="button"
                  onClick={() => setFormData({...formData, genre})}
                  className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                    formData.genre === genre 
                      ? "bg-neon-red text-white" 
                      : "bg-surface-highlight text-chrome-mid hover:text-white border border-white/10"
                  }`}
                  data-testid={`request-genre-${genre.toLowerCase().replace(" ", "-")}`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">MESSAGE (OPTIONAL)</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              className="radio-input min-h-[100px] resize-none"
              placeholder="Dedicate this song to someone or share why you love it..."
              data-testid="request-message"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">YOUR NAME *</label>
              <input
                type="text"
                value={formData.user_name}
                onChange={(e) => setFormData({...formData, user_name: e.target.value})}
                className="radio-input"
                required
                data-testid="request-name"
              />
            </div>
            <div>
              <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">EMAIL *</label>
              <input
                type="email"
                value={formData.user_email}
                onChange={(e) => setFormData({...formData, user_email: e.target.value})}
                className="radio-input"
                required
                data-testid="request-email"
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="chrome-btn w-full"
            disabled={loading}
            data-testid="request-submit-btn"
          >
            {loading ? "Submitting..." : "Submit Request"}
          </button>
        </form>
      </div>
    </main>
  );
};

// Media Submission Page (Song & Video)
const SubmitPage = () => {
  const [submissionType, setSubmissionType] = useState("song");
  const [formData, setFormData] = useState({
    artist_name: "",
    artist_email: "",
    artist_phone: "",
    artist_bio: "",
    artist_website: "",
    artist_social: "",
    track_title: "",
    genre: "Blues",
    release_date: "",
    media_url: "",
    cover_art_url: "",
    lyrics: "",
    description: "",
    rights_confirmation: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [coverArtFile, setCoverArtFile] = useState(null);
  const [coverArtPreview, setCoverArtPreview] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isMediaDragging, setIsMediaDragging] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const fileInputRef = useRef(null);
  const mediaFileRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        artist_name: user.name,
        artist_email: user.email
      }));
    }
  }, [user]);

  const genres = ["Blues", "R&B", "Jazz", "Gospel", "Rock", "Soft Rock", "Neo Soul", "Techno", "Hip Hop", "Electronic", "Other"];

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleCoverArtFile(file);
    } else {
      toast.error("Please drop an image file (JPG, PNG, etc.)");
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleCoverArtFile(file);
    }
  };

  // Process the cover art file
  const handleCoverArtFile = (file) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }
    setCoverArtFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setCoverArtPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Remove cover art
  const removeCoverArt = () => {
    setCoverArtFile(null);
    setCoverArtPreview(null);
    setFormData({...formData, cover_art_url: ""});
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.rights_confirmation) {
      toast.error("Please confirm you have the rights to submit this content");
      return;
    }
    if (!mediaFile && !formData.media_url) {
      toast.error("Please upload a file or provide a streaming link");
      return;
    }
    setLoading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("submission_type", submissionType);
      fd.append("artist_name", formData.artist_name);
      fd.append("artist_email", formData.artist_email);
      fd.append("track_title", formData.track_title);
      fd.append("genre", formData.genre);
      fd.append("rights_confirmation", formData.rights_confirmation);
      fd.append("artist_phone", formData.artist_phone || "");
      fd.append("artist_bio", formData.artist_bio || "");
      fd.append("artist_website", formData.artist_website || "");
      fd.append("artist_social", formData.artist_social || "");
      fd.append("release_date", formData.release_date || "");
      fd.append("description", formData.description || "");
      fd.append("lyrics", formData.lyrics || "");
      fd.append("media_url", formData.media_url || "");
      
      if (mediaFile) {
        fd.append("media_file", mediaFile);
      }
      if (coverArtFile) {
        fd.append("cover_art_file", coverArtFile);
      }
      
      await axios.post(`${API}/submissions/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(pct);
        }
      });
      setSubmitted(true);
      toast.success("Submission received! We'll review it soon.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  if (submitted) {
    return (
      <main className="pt-24 pb-32 px-6" data-testid="submit-success-page">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-24 h-24 bg-neon-red mx-auto mb-8 flex items-center justify-center"
          >
            <Check className="w-12 h-12 text-white" />
          </motion.div>
          <h1 className="font-display text-4xl md:text-6xl chrome-text mb-4">SUBMISSION RECEIVED</h1>
          <p className="text-chrome-mid mb-8 text-lg">
            Thank you for submitting your {submissionType} to Hot Live 95 Detroit! Our team will review your submission and get back to you within 5-7 business days.
          </p>
          <div className="bg-surface border border-white/10 p-6 mb-8">
            <h3 className="font-display text-lg mb-2">WHAT'S NEXT?</h3>
            <ul className="text-chrome-mid text-sm space-y-2 text-left">
              <li className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-neon-red mt-0.5 flex-shrink-0" />
                <span>Our music directors will review your submission</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-neon-red mt-0.5 flex-shrink-0" />
                <span>If approved, your {submissionType} will be added to our rotation</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-neon-red mt-0.5 flex-shrink-0" />
                <span>You'll receive an email notification about your submission status</span>
              </li>
            </ul>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => { setSubmitted(false); setFormData({ ...formData, track_title: "", media_url: "", description: "", lyrics: "" }); }}
              className="primary-btn"
              data-testid="submit-another-btn"
            >
              Submit Another
            </button>
            <Link to="/" className="outline-btn" data-testid="back-home-btn">
              Back to Home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-32 px-6" data-testid="submit-page">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-2">GET ON THE AIR</p>
          <h1 className="font-display text-4xl md:text-6xl chrome-text mb-4">SUBMIT YOUR MUSIC</h1>
          <p className="text-chrome-mid text-lg max-w-2xl">
            Are you an artist looking to get your music heard? Submit your songs or music videos for airplay consideration on Hot Live 95 Detroit.
          </p>
        </motion.div>

        {/* Submission Type Selector */}
        <div className="flex gap-4 mb-12">
          <button
            onClick={() => setSubmissionType("song")}
            className={`flex-1 p-6 border transition-all ${
              submissionType === "song" 
                ? "bg-neon-red/10 border-neon-red" 
                : "bg-surface border-white/10 hover:border-white/30"
            }`}
            data-testid="select-song-type"
          >
            <Music2 className={`w-8 h-8 mb-3 ${submissionType === "song" ? "text-neon-red" : "text-chrome-mid"}`} />
            <h3 className="font-display text-xl mb-1">SONG</h3>
            <p className="text-chrome-mid text-sm">Submit audio tracks for radio airplay</p>
          </button>
          <button
            onClick={() => setSubmissionType("video")}
            className={`flex-1 p-6 border transition-all ${
              submissionType === "video" 
                ? "bg-neon-red/10 border-neon-red" 
                : "bg-surface border-white/10 hover:border-white/30"
            }`}
            data-testid="select-video-type"
          >
            <Video className={`w-8 h-8 mb-3 ${submissionType === "video" ? "text-neon-red" : "text-chrome-mid"}`} />
            <h3 className="font-display text-xl mb-1">VIDEO</h3>
            <p className="text-chrome-mid text-sm">Submit music videos for video rotation</p>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Artist Information */}
          <div className="bg-surface border border-white/10 p-6 md:p-8">
            <h3 className="font-display text-xl mb-6 flex items-center gap-3">
              <User className="w-5 h-5 text-neon-red" />
              ARTIST INFORMATION
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                  <span className="flex items-center gap-2"><User className="w-3 h-3" /> ARTIST/BAND NAME *</span>
                </label>
                <input
                  type="text"
                  value={formData.artist_name}
                  onChange={(e) => setFormData({...formData, artist_name: e.target.value})}
                  className="radio-input"
                  required
                  placeholder="Your artist or band name"
                  data-testid="submit-artist-name"
                />
              </div>
              <div>
                <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                  <span className="flex items-center gap-2"><Mail className="w-3 h-3" /> EMAIL *</span>
                </label>
                <input
                  type="email"
                  value={formData.artist_email}
                  onChange={(e) => setFormData({...formData, artist_email: e.target.value})}
                  className="radio-input"
                  required
                  placeholder="your@email.com"
                  data-testid="submit-artist-email"
                />
              </div>
              <div>
                <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                  <span className="flex items-center gap-2"><Phone className="w-3 h-3" /> PHONE (OPTIONAL)</span>
                </label>
                <input
                  type="tel"
                  value={formData.artist_phone}
                  onChange={(e) => setFormData({...formData, artist_phone: e.target.value})}
                  className="radio-input"
                  placeholder="+1 (555) 000-0000"
                  data-testid="submit-artist-phone"
                />
              </div>
              <div>
                <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                  <span className="flex items-center gap-2"><Globe className="w-3 h-3" /> WEBSITE (OPTIONAL)</span>
                </label>
                <input
                  type="url"
                  value={formData.artist_website}
                  onChange={(e) => setFormData({...formData, artist_website: e.target.value})}
                  className="radio-input"
                  placeholder="https://yourwebsite.com"
                  data-testid="submit-artist-website"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                SOCIAL MEDIA LINKS (OPTIONAL)
              </label>
              <input
                type="text"
                value={formData.artist_social}
                onChange={(e) => setFormData({...formData, artist_social: e.target.value})}
                className="radio-input"
                placeholder="Instagram, Twitter, TikTok, etc."
                data-testid="submit-artist-social"
              />
            </div>

            <div className="mt-6">
              <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                ARTIST BIO (OPTIONAL)
              </label>
              <textarea
                value={formData.artist_bio}
                onChange={(e) => setFormData({...formData, artist_bio: e.target.value})}
                className="radio-input min-h-[100px] resize-none"
                placeholder="Tell us about yourself or your band..."
                data-testid="submit-artist-bio"
              />
            </div>
          </div>

          {/* Track Information */}
          <div className="bg-surface border border-white/10 p-6 md:p-8">
            <h3 className="font-display text-xl mb-6 flex items-center gap-3">
              {submissionType === "song" ? <Music2 className="w-5 h-5 text-neon-red" /> : <Video className="w-5 h-5 text-neon-red" />}
              {submissionType === "song" ? "TRACK" : "VIDEO"} INFORMATION
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                  {submissionType === "song" ? "SONG" : "VIDEO"} TITLE *
                </label>
                <input
                  type="text"
                  value={formData.track_title}
                  onChange={(e) => setFormData({...formData, track_title: e.target.value})}
                  className="radio-input"
                  required
                  placeholder={`Your ${submissionType} title`}
                  data-testid="submit-track-title"
                />
              </div>
              <div>
                <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                  RELEASE DATE (OPTIONAL)
                </label>
                <input
                  type="date"
                  value={formData.release_date}
                  onChange={(e) => setFormData({...formData, release_date: e.target.value})}
                  className="radio-input"
                  data-testid="submit-release-date"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">GENRE *</label>
              <div className="flex flex-wrap gap-2">
                {genres.map((genre) => (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => setFormData({...formData, genre})}
                    className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                      formData.genre === genre 
                        ? "bg-neon-red text-white" 
                        : "bg-surface-highlight text-chrome-mid hover:text-white border border-white/10"
                    }`}
                    data-testid={`submit-genre-${genre.toLowerCase().replace(" ", "-")}`}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                <span className="flex items-center gap-2">
                  <Upload className="w-3 h-3" /> 
                  UPLOAD {submissionType === "song" ? "AUDIO" : "VIDEO"} FILE
                </span>
              </label>
              
              {mediaFile ? (
                <div className="bg-black/50 border border-neon-red/30 p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {submissionType === "song" ? <Music2 className="w-5 h-5 text-neon-red" /> : <Video className="w-5 h-5 text-neon-red" />}
                    <div>
                      <p className="text-white text-sm">{mediaFile.name}</p>
                      <p className="text-chrome-dark text-xs">{(mediaFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setMediaFile(null); if (mediaFileRef.current) mediaFileRef.current.value = ""; }}
                    className="p-2 hover:bg-red-500/20 text-red-400"
                    data-testid="remove-media-file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsMediaDragging(true); }}
                  onDragLeave={() => setIsMediaDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsMediaDragging(false);
                    const f = e.dataTransfer.files[0];
                    if (f) {
                      const ext = f.name.split('.').pop().toLowerCase();
                      const allowed = submissionType === "song" ? ["mp3", "wav"] : ["mp4"];
                      if (!allowed.includes(ext)) {
                        toast.error(`Only ${allowed.join(", ")} files allowed`);
                        return;
                      }
                      const maxSize = submissionType === "song" ? 50 * 1024 * 1024 : 500 * 1024 * 1024;
                      if (f.size > maxSize) {
                        toast.error(`File too large. Max ${maxSize / (1024 * 1024)}MB`);
                        return;
                      }
                      setMediaFile(f);
                    }
                  }}
                  onClick={() => mediaFileRef.current?.click()}
                  className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                    isMediaDragging 
                      ? 'border-neon-red bg-neon-red/10' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                  data-testid="media-file-dropzone"
                >
                  <Upload className="w-10 h-10 mx-auto mb-3 text-chrome-mid" />
                  <p className="text-chrome-mid text-sm mb-1">
                    <span className="text-neon-red">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-chrome-dark text-xs">
                    {submissionType === "song" 
                      ? "MP3, WAV (max 50MB)" 
                      : "MP4 (max 500MB)"
                    }
                  </p>
                </div>
              )}
              
              <input
                ref={mediaFileRef}
                type="file"
                accept={submissionType === "song" ? ".mp3,.wav" : ".mp4"}
                onChange={(e) => {
                  const f = e.target.files[0];
                  if (f) {
                    const maxSize = submissionType === "song" ? 50 * 1024 * 1024 : 500 * 1024 * 1024;
                    if (f.size > maxSize) {
                      toast.error(`File too large. Max ${maxSize / (1024 * 1024)}MB`);
                      return;
                    }
                    setMediaFile(f);
                  }
                }}
                className="hidden"
                data-testid="media-file-input"
              />

              {/* Upload Progress */}
              {loading && uploadProgress > 0 && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-chrome-mid mb-1">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-black/50 rounded-full h-2">
                    <div 
                      className="bg-neon-red h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <p className="text-chrome-dark text-xs mt-3 border-t border-white/5 pt-3">
                OR provide a streaming link instead:
              </p>
              <input
                type="url"
                value={formData.media_url}
                onChange={(e) => setFormData({...formData, media_url: e.target.value})}
                className="radio-input mt-2"
                placeholder={submissionType === "song" 
                  ? "SoundCloud, Spotify, Bandcamp link" 
                  : "YouTube, Vimeo link"
                }
                data-testid="submit-media-url"
              />
            </div>

            <div className="mt-6">
              <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                COVER ART (OPTIONAL)
              </label>
              
              {coverArtPreview ? (
                <div className="relative w-full max-w-[200px]">
                  <img 
                    src={coverArtPreview} 
                    alt="Cover art preview" 
                    className="w-full h-auto border border-white/20"
                  />
                  <button
                    type="button"
                    onClick={removeCoverArt}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-neon-red text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    data-testid="remove-cover-art"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-chrome-mid mt-2">{coverArtFile?.name}</p>
                </div>
              ) : (
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                    isDragging 
                      ? 'border-neon-red bg-neon-red/10' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                  data-testid="cover-art-dropzone"
                >
                  <Upload className="w-10 h-10 mx-auto mb-3 text-chrome-mid" />
                  <p className="text-chrome-mid text-sm mb-1">
                    <span className="text-neon-red">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-chrome-dark text-xs">PNG, JPG, WEBP (max 5MB)</p>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="cover-art-input"
              />
            </div>

            <div className="mt-6">
              <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                DESCRIPTION
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="radio-input min-h-[100px] resize-none"
                placeholder={`Tell us about this ${submissionType}. What inspired it? What makes it special?`}
                data-testid="submit-description"
              />
            </div>

            {submissionType === "song" && (
              <div className="mt-6">
                <label className="font-mono text-xs tracking-widest text-chrome-mid block mb-2">
                  <span className="flex items-center gap-2"><FileText className="w-3 h-3" /> LYRICS (OPTIONAL)</span>
                </label>
                <textarea
                  value={formData.lyrics}
                  onChange={(e) => setFormData({...formData, lyrics: e.target.value})}
                  className="radio-input min-h-[150px] resize-none font-mono text-sm"
                  placeholder="Paste your song lyrics here..."
                  data-testid="submit-lyrics"
                />
              </div>
            )}
          </div>

          {/* Rights Confirmation */}
          <div className="bg-surface border border-white/10 p-6 md:p-8">
            <label className="flex items-start gap-4 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.rights_confirmation}
                onChange={(e) => setFormData({...formData, rights_confirmation: e.target.checked})}
                className="w-6 h-6 mt-1 accent-neon-red"
                data-testid="submit-rights-checkbox"
              />
              <div>
                <p className="font-display text-sm mb-1">I CONFIRM THAT: *</p>
                <ul className="text-chrome-mid text-sm space-y-1">
                  <li>• I am the creator/rights holder of this {submissionType}</li>
                  <li>• I have the legal right to submit this content for broadcast</li>
                  <li>• I grant Hot Live 95 Detroit permission to play this {submissionType} on air</li>
                  <li>• All information provided is accurate</li>
                </ul>
              </div>
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              type="submit" 
              className="chrome-btn flex-1 flex items-center justify-center gap-2"
              disabled={loading || !formData.rights_confirmation}
              data-testid="submit-form-btn"
            >
              <Upload className="w-5 h-5" />
              {loading ? "SUBMITTING..." : `SUBMIT ${submissionType.toUpperCase()}`}
            </button>
          </div>

          {/* Info Box */}
          <div className="bg-neon-red/10 border border-neon-red/30 p-6">
            <h4 className="font-display text-sm mb-2 text-neon-red">SUBMISSION GUIDELINES</h4>
            <ul className="text-chrome-mid text-sm space-y-1">
              <li>• All submissions are reviewed by our music directors</li>
              <li>• Response time is typically 5-7 business days</li>
              <li>• We prioritize artists with Detroit connections</li>
              <li>• Original content only - no covers without proper licensing</li>
              <li>• Questions? Email us at submissions@hotlive95detroit.com</li>
            </ul>
          </div>
        </form>
      </div>
    </main>
  );
};

// Subscription Success Page - After Square Payment
const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyAndActivateSubscription = async () => {
      try {
        // Get the transaction ID from Square redirect (if any)
        const transactionId = searchParams.get("transactionId");
        const checkoutId = searchParams.get("checkoutId");
        
        // If user is logged in, activate their subscription
        if (user) {
          // Call complete payment endpoint
          const response = await axios.post(`${API}/payments/complete?order_id=${user.id}`);
          if (response.data.success) {
            setStatus("success");
          } else {
            setStatus("success"); // Still show success as payment was made
          }
        } else {
          // No user logged in - show general success
          setStatus("success");
        }
      } catch (err) {
        console.error("Error verifying subscription:", err);
        setStatus("success"); // Show success anyway - payment was likely made
      }
    };

    verifyAndActivateSubscription();
  }, [searchParams, user]);

  if (status === "loading") {
    return (
      <main className="pt-24 pb-32 px-6 min-h-screen flex items-center justify-center" data-testid="subscription-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-neon-red border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-chrome-mid">Processing your payment...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-32 px-6" data-testid="subscription-success-page">
      <div className="max-w-2xl mx-auto text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="w-24 h-24 bg-neon-red mx-auto mb-8 flex items-center justify-center"
        >
          <Check className="w-12 h-12 text-white" />
        </motion.div>
        
        <h1 className="font-display text-4xl md:text-6xl chrome-text mb-4">WELCOME TO THE FAMILY!</h1>
        
        <p className="text-chrome-mid text-lg mb-8">
          Your subscription to Hot Live 95 Detroit is now active! You have full access to all exclusive live video podcasts and premium content.
        </p>

        <div className="bg-surface border border-neon-red/20 p-6 mb-8">
          <h3 className="font-display text-xl mb-4 text-neon-red">YOUR SUBSCRIBER BENEFITS</h3>
          <ul className="text-chrome-mid text-left space-y-3">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-neon-red mt-0.5 flex-shrink-0" />
              <span>Unlimited access to Live Video Podcasts</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-neon-red mt-0.5 flex-shrink-0" />
              <span>Exclusive behind-the-scenes content</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-neon-red mt-0.5 flex-shrink-0" />
              <span>Ad-free viewing experience</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-neon-red mt-0.5 flex-shrink-0" />
              <span>Early access to new shows and features</span>
            </li>
          </ul>
        </div>

        <div className="bg-black/30 border border-white/10 p-4 mb-8">
          <p className="text-chrome-mid text-sm">
            <strong className="text-white">Subscription Details:</strong><br />
            $5.00/month • Billed monthly • Cancel anytime
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/podcast" className="chrome-btn px-8 py-4" data-testid="watch-podcast-btn">
            Watch Live Podcast
          </Link>
          <Link to="/" className="outline-btn px-8 py-4" data-testid="go-home-btn">
            Explore More
          </Link>
        </div>

        <p className="text-xs text-chrome-dark mt-8">
          A confirmation email has been sent to your email address.
          <br />
          Questions? Contact support@hotlive95detroit.com
        </p>
      </div>
    </main>
  );
};

// Live Podcast Page - Subscribers Only
const LivePodcastPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [streamConfig, setStreamConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("register");

  useEffect(() => {
    fetchStreamConfig();
  }, []);

  // Close modal when user logs in
  useEffect(() => {
    if (token && user) {
      setShowAuthModal(false);
    }
  }, [token, user]);

  const fetchStreamConfig = async () => {
    try {
      const response = await axios.get(`${API}/podcast/stream-config`);
      setStreamConfig(response.data);
    } catch (e) {
      console.error("Failed to fetch stream config");
    } finally {
      setLoading(false);
    }
  };

  // Check if user is a subscriber
  const isSubscriber = user?.is_subscriber === true;

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <main className="pt-24 pb-32 px-6 min-h-screen flex items-center justify-center" data-testid="podcast-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-neon-red border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-chrome-mid">Loading...</p>
        </div>
      </main>
    );
  }

  // Not logged in - show login prompt
  if (!token) {
    return (
      <main className="pt-24 pb-32 px-6 min-h-screen" data-testid="podcast-page">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-surface border border-white/10 p-8 md:p-12"
          >
            <Lock className="w-16 h-16 text-neon-red mx-auto mb-6" />
            <h1 className="font-display text-3xl md:text-4xl chrome-text mb-4">SUBSCRIBERS ONLY</h1>
            <p className="text-chrome-mid mb-8">
              Access to Live Video Podcasts is exclusively available to Hot Live 95 Detroit subscribers.
            </p>
            <div className="bg-black/30 border border-neon-red/20 p-6 mb-8">
              <h3 className="font-display text-xl text-neon-red mb-4">SUBSCRIPTION BENEFITS</h3>
              <ul className="text-chrome-mid text-left space-y-2 max-w-md mx-auto">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-neon-red flex-shrink-0" />
                  <span>Unlimited Live Video Podcasts</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-neon-red flex-shrink-0" />
                  <span>Exclusive Behind-the-Scenes Content</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-neon-red flex-shrink-0" />
                  <span>Ad-Free Viewing Experience</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-neon-red flex-shrink-0" />
                  <span>Early Access to New Shows</span>
                </li>
              </ul>
              <p className="text-neon-red font-display text-2xl mt-6">$5/MONTH</p>
            </div>
            <button
              onClick={() => setShowAuthModal(true)}
              className="chrome-btn px-8 py-4"
              data-testid="subscribe-to-watch-btn"
            >
              Subscribe to Watch
            </button>
          </motion.div>
        </div>

        {/* Auth Modal */}
        <AnimatePresence>
          {showAuthModal && (
            <AuthModal 
              mode={authMode} 
              setMode={setAuthMode} 
              onClose={() => setShowAuthModal(false)} 
            />
          )}
        </AnimatePresence>
      </main>
    );
  }

  // Logged in but not a subscriber
  if (!isSubscriber) {
    return (
      <main className="pt-24 pb-32 px-6 min-h-screen" data-testid="podcast-page">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-surface border border-white/10 p-8 md:p-12"
          >
            <Lock className="w-16 h-16 text-neon-red mx-auto mb-6" />
            <h1 className="font-display text-3xl md:text-4xl chrome-text mb-4">UPGRADE TO ACCESS</h1>
            <p className="text-chrome-mid mb-4">
              Welcome back, <span className="text-white">{user?.name}</span>!
            </p>
            <p className="text-chrome-mid mb-8">
              Upgrade to a premium subscription to access exclusive Live Video Podcasts.
            </p>
            <div className="bg-black/30 border border-neon-red/20 p-6 mb-8">
              <p className="text-neon-red font-display text-2xl">ONLY $5/MONTH</p>
              <p className="text-chrome-mid text-sm mt-2">Cancel anytime</p>
            </div>
            <button
              onClick={async () => {
                try {
                  toast.info("Redirecting to payment...");
                  const response = await axios.post(`${API}/payments/create-payment-link`, {
                    user_id: user.id,
                    email: user.email,
                    name: user.name
                  });
                  if (response.data.payment_link_url) {
                    window.location.href = response.data.payment_link_url;
                  }
                } catch (err) {
                  toast.error("Failed to create payment link");
                }
              }}
              className="chrome-btn px-8 py-4"
              data-testid="upgrade-subscription-btn"
            >
              Upgrade Now - $5/month
            </button>
          </motion.div>
        </div>
      </main>
    );
  }

  // Subscriber - show the podcast
  return (
    <main className="pt-24 pb-32 px-6 min-h-screen" data-testid="podcast-page">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Tv className="w-8 h-8 text-neon-red" />
              <h1 className="font-display text-3xl md:text-5xl chrome-text">LIVE PODCAST</h1>
            </div>
            <p className="text-chrome-mid">Exclusive content for Hot Live 95 Detroit subscribers</p>
            {streamConfig?.is_live && (
              <div className="inline-flex items-center gap-2 mt-4 bg-neon-red/20 px-4 py-2 border border-neon-red">
                <span className="w-3 h-3 bg-neon-red rounded-full animate-pulse"></span>
                <span className="font-mono text-sm text-neon-red">LIVE NOW</span>
              </div>
            )}
          </div>

          {/* Video Player */}
          <div className="bg-black border border-white/10 mb-8">
            {loading ? (
              <div className="aspect-video flex items-center justify-center bg-surface">
                <div className="w-12 h-12 border-4 border-neon-red border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : streamConfig?.youtube_url ? (
              <div className="aspect-video">
                <iframe
                  src={streamConfig.youtube_url.includes('embed') 
                    ? streamConfig.youtube_url 
                    : streamConfig.youtube_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')
                  }
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Hot Live 95 Detroit - Live Podcast"
                  data-testid="podcast-video-player"
                ></iframe>
              </div>
            ) : (
              <div className="aspect-video flex flex-col items-center justify-center bg-surface">
                <Tv className="w-16 h-16 text-chrome-dark mb-4" />
                <p className="text-chrome-mid text-lg">No stream currently scheduled</p>
                <p className="text-chrome-dark text-sm mt-2">Check back soon for our next live podcast!</p>
              </div>
            )}
          </div>

          {/* Stream Info */}
          {streamConfig && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-surface border border-white/10 p-6">
                <h3 className="font-display text-xl chrome-text mb-4">CURRENT SHOW</h3>
                <p className="text-white text-lg mb-2">{streamConfig.show_title || "Hot Live 95 Live Podcast"}</p>
                <p className="text-chrome-mid">{streamConfig.show_description || "Live from Detroit - The Motor City's hottest radio station bringing you the best in Blues, R&B, Jazz, and more!"}</p>
              </div>
              <div className="bg-surface border border-white/10 p-6">
                <h3 className="font-display text-xl chrome-text mb-4">SCHEDULE</h3>
                <p className="text-chrome-mid mb-2">
                  <span className="text-white">Next Live Show:</span> {streamConfig.next_show_time || "Check our schedule"}
                </p>
                <p className="text-chrome-mid">
                  <span className="text-white">Host:</span> {streamConfig.host_name || "DJ Hot Live"}
                </p>
                <Link to="/schedule" className="inline-flex items-center gap-2 text-neon-red hover:underline mt-4">
                  View Full Schedule <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {/* Subscriber Badge */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 bg-neon-red/10 border border-neon-red/30 px-4 py-2">
              <Check className="w-5 h-5 text-neon-red" />
              <span className="text-chrome-mid text-sm">Premium Subscriber</span>
            </div>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

// App Content with routing logic
function AppContent() {
  const location = window.location.pathname;
  const isAdminRoute = location === '/admin';
  
  return (
    <>
      {!isAdminRoute && (
        <>
          <LogoBackground />
          <NoiseOverlay />
          <Navigation />
        </>
      )}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/stream" element={<StreamPage />} />
        <Route path="/stream-test" element={<StreamTestPage />} />
        <Route path="/segments" element={<SegmentsPage />} />
        <Route path="/segments/:slug" element={<SegmentDetailPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/request" element={<RequestPage />} />
        <Route path="/submit" element={<SubmitPage />} />
        <Route path="/news" element={<WeatherNewsPage />} />
        <Route path="/djs" element={<DJsPage />} />
        <Route path="/djs/:djId" element={<DJDetailPage />} />
        <Route path="/listeners" element={<ListenerMapPage />} />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="/podcast" element={<LivePodcastPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
          },
        }}
      />
    </>
  );
}

export default App;
